import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Select from 'src/components/ui/Select/Select';
import { Control, useController, useWatch } from 'react-hook-form';
import styles from './Conditions.module.scss';
import { ProductFormValues } from '../ProductFormTypes';
import Icon from '../../../ui/Icon/Icon';

interface ConditionsProps {
  disabled?: boolean;
  lockedUnlessDraft?: boolean;
  control: Control<ProductFormValues>;
  isEditMode?: boolean;
}

interface ProductTypeOption {
  label: string;
  value: string;
}

const PRODUCT_TYPES = {
  NONE: 'none',
  NON_SHIPPABLE: 'is_non_shippable',
  PRODUCT_SET: 'is_product_set',
};

const Conditions: React.FC<ConditionsProps> = ({ disabled, lockedUnlessDraft, control, isEditMode }) => {
  const { t } = useTranslation();

  const options = {
    default: { label: t('product.storable_product'), value: PRODUCT_TYPES.NONE },
    nonShippable: { label: t('product.digital_product'), value: PRODUCT_TYPES.NON_SHIPPABLE },
    productSet: { label: t('general.product_set'), value: PRODUCT_TYPES.PRODUCT_SET },
  };

  const isNonShippable = !!useWatch({ control, name: 'isNonShippable' });
  const isProductSet = !!useWatch({ control, name: 'isProductSet' });

  let selectedOption;
  if (isNonShippable) {
    selectedOption = options.nonShippable;
  } else if (isProductSet) {
    selectedOption = options.productSet;
  } else {
    selectedOption = options.default;
  }

  const { field: isNonShippableField } = useController({
    name: 'isNonShippable',
    control,
  });

  const { field: isProductSetField } = useController({
    name: 'isProductSet',
    control,
  });

  const handleProductTypeChange = (selected: ProductTypeOption) => {
    const isNonShippable = selected.value === PRODUCT_TYPES.NON_SHIPPABLE;
    const isProductSet = selected.value === PRODUCT_TYPES.PRODUCT_SET;

    isNonShippableField.onChange(isNonShippable);
    isProductSetField.onChange(isProductSet);
  };

  const selectOptions = [options.default, options.nonShippable, options.productSet];

  return (
    <div className={classNames(styles.root)}>
      <div className="grid grid-cols-4 items-center gap-16 mb-16">
        <div className="col-span-1 typo-text-14--bold text-faded">
          {t('product.product_type')}
          {isEditMode && (
            <div className="relative group inline-flex">
              <Icon name="info" width={16} height={16} className="text-faded cursor-pointer" />
              <div
                className="
                  absolute left-full top-1/2 -translate-y-1/2 ml-2
                  p-6 bg-color-grey-400 text-white typo-text-12 rounded
                  opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10
                  max-w-xs w-max whitespace-normal font-sans
                ">
                {t('product.type_info')}
              </div>
            </div>
          )}
        </div>
        <Select
          isInfoDisplay
          disabled={disabled || lockedUnlessDraft}
          className="col-span-3"
          defaultValue={selectedOption}
          options={selectOptions}
          onChange={handleProductTypeChange}
        />
      </div>
    </div>
  );
};

export default Conditions;
